import { useState, useEffect } from 'react';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import { ArrowLeft, ArrowRight, ChevronRight, Users, Link2 } from 'lucide-react';
import { DubbingDetailsForm } from '../components/DubbingDetailsForm';
import { Button } from "../components/ui/button";
import { useAuth } from '../contexts/AuthContext';
import { usePublish } from '../contexts/PublishContext';
import { ShareModal } from '../components/ShareModal';
import { toast } from 'sonner';
import styles from '../styles/Publish.module.css';
import { useDubbingDetails } from '../hooks/useShowDetails';

// Use the same interfaces as DubbingDetailsForm
interface DubbingInfo {
  studioAssigned: string;
  assignedDate: string;
  stages: 'SCRIPTING' | 'CASTING' | 'RECORDING' | 'MIXING' | 'FINALIZING' | 'COMPLETED';
  notes?: string;
}

interface WriterInfo {
  agencyType: string;
  name: string;
  assignedDate?: string;
  stages: 'NOT_STARTED' | 'FIRST_DRAFT' | 'REVIEW' | 'REWORKING' | 'FINAL_VERSION';
}

interface QcInfo {
  type: string;
  qcAgency?: string;
  qcDone: boolean;
  remarksSheetLink?: string;
  owner?: string;
  passedStatus: 'PASSED' | 'FAILED' | 'PENDING';
}

interface NameChangeInfo {
  changedToLocalizedName?: string;
  status: 'NOT_APPLICABLE' | 'NOT_STARTED' | 'PARTIALLY_COMPLETED' | 'COMPLETED';
}

interface LocalizedVersion {
  language: string;
  status: 'NOT_STARTED' | 'IN_PROGRESS' | 'ON_HOLD' | 'COMPLETED' | 'RELEASED';
  dubbingInfo?: DubbingInfo;
  writerInfo?: WriterInfo;
  qcInfo?: QcInfo;
  dubbedLinkFinal?: string;
  localizationSheetLink?: string;
  nameChangeInfo?: NameChangeInfo;
  missingAdditionalCharacters?: string[];
  localizedEpsDriveLink?: string;
}

interface DubbingProject {
  id?: string;
  showId: string;
  readyForLocalization: boolean;
  showAssignedDate?: string;
  growthPriority: number | null;
  scrapeStatus: boolean;
  srtStatus: boolean;
  localizedVersions: LocalizedVersion[];
}

interface DubbingDetailsData {
  projects: DubbingProject[];
}

export default function DubbingDetails() {
  const navigate = useNavigate();
  const location = useLocation();
  const { showId } = useParams<{ showId: string }>();
  const { isAuth, loading } = useAuth();
  const { state, updateState } = usePublish();
  const [showShareModal, setShowShareModal] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [initialData, setInitialData] = useState<any>(null);
  const dubbingApi = useDubbingDetails(showId || null);

  // Sync API data with local state when projects are loaded
  useEffect(() => {
    if (dubbingApi.projects && dubbingApi.projects.length > 0) {
      const newData = { 
        projects: dubbingApi.projects.map(project => ({
          id: project.id,
          showId: project.showId,
          readyForLocalization: project.readyForLocalization,
          showAssignedDate: project.showAssignedDate,
          growthPriority: project.growthPriority,
          scrapeStatus: project.scrapeStatus,
          srtStatus: project.srtStatus,
          localizedVersions: project.localizedVersions
        }))
      };
      updateState({ dubbingDetails: newData });
      // Store initial data for change comparison
      setInitialData(JSON.stringify(newData));
      setHasChanges(false);
    }
  }, [dubbingApi.projects]);

  // Track changes to dubbing data
  useEffect(() => {
    if (initialData && state.dubbingDetails) {
      const currentData = JSON.stringify(state.dubbingDetails);
      setHasChanges(currentData !== initialData);
    }
  }, [state.dubbingDetails, initialData]);

  // Redirect to home if not authenticated
  useEffect(() => {
    if (!loading && !isAuth) {
      navigate('/', { replace: true });
    }
  }, [isAuth, loading, navigate]);

  // Show loading state while checking authentication
  if (loading) {
    return (
      <div className="h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  // Don't render if not authenticated (will redirect)
  if (!isAuth) {
    return null;
  }

  const handleSave = async () => {
    if (!showId) {
      toast.error('No show ID available');
      return;
    }

    try {
      toast.loading('Saving dubbing details...', { id: 'save-dubbing' });
      
      // Save each project to the server
      for (const project of state.dubbingDetails.projects) {
        if (project.id) {
          // Update existing project
          await dubbingApi.updateProject(project.id, project);
        } else {
          // Create new project
          await dubbingApi.createProject(project);
        }
      }
      
      // Reset change tracking after successful save
      setInitialData(JSON.stringify(state.dubbingDetails));
      setHasChanges(false);
      
      toast.success('Dubbing details saved successfully!', { id: 'save-dubbing' });
    } catch (error) {
      console.error('Failed to save dubbing details:', error);
      toast.error('Failed to save dubbing details. Please try again.', { id: 'save-dubbing' });
    }
  };

  const handleContinue = async () => {
    // Only auto-save if there are changes
    if (showId && hasChanges && state.dubbingDetails.projects.length > 0) {
      try {
        toast.loading('Saving changes...', { id: 'auto-save' });
        
        // Save each project to the server
        for (const project of state.dubbingDetails.projects) {
          if (project.id) {
            // Update existing project
            await dubbingApi.updateProject(project.id, project);
          } else {
            // Create new project
            await dubbingApi.createProject(project);
          }
        }
        
        // Reset change tracking after successful save
        setInitialData(JSON.stringify(state.dubbingDetails));
        setHasChanges(false);
        
        toast.success('Changes saved successfully!', { id: 'auto-save' });
      } catch (error) {
        console.error('Failed to auto-save dubbing details:', error);
        toast.error('Failed to save changes', { id: 'auto-save' });
        return; // Don't navigate if save failed
      }
    }
    navigate(`/publish/${showId || 'new-show'}/thumbnails`);
  };

  const handleBack = () => {
    navigate(`/publish/${showId || 'new-show'}/promo-details`);
  };

  return (
    <div className={styles.container}>
      {/* Header */}
      <div className={styles.header}>
        <div className={styles.headerContent}>
          {/* Left Section */}
          <div className={styles.leftSection}>
            <h1 className={styles.navTitle}>Publish</h1>
          </div>
        
          {/* Center Section */}
          <div className={styles.centerSection}>
            <Button
              variant={location.pathname === `/publish/${showId}` ? 'secondary' : 'ghost'}
              size="sm"
              className={styles.navButton}
              onClick={() => navigate(`/publish/${showId}`)}
            >
              Show Details
            </Button>
            <Button 
              variant={location.pathname === `/publish/${showId || 'new-show'}/internal-details` ? 'secondary' : 'ghost'}
              size="sm"
              className={styles.navButton}
              onClick={() => navigate(`/publish/${showId || 'new-show'}/internal-details`)}
            >
              Internal Details
            </Button>
            {/* Promo Details — hidden per content team request, re-enable when needed */}
            {/* <Button
              variant={location.pathname === `/publish/${showId || 'new-show'}/promo-details` ? 'secondary' : 'ghost'}
              size="sm"
              className={styles.navButton}
              onClick={() => navigate(`/publish/${showId || 'new-show'}/promo-details`)}
            >
              Promo Details
            </Button> */}
            {/* Dubbing Details — hidden per content team request, re-enable when needed */}
            {/* <Button
              variant={location.pathname === `/publish/${showId || 'new-show'}/dubbing-details` ? 'secondary' : 'ghost'}
              size="sm"
              className={styles.navButton}
              onClick={() => navigate(`/publish/${showId || 'new-show'}/dubbing-details`)}
            >
              Dubbing Details
            </Button> */}
            <Button 
              variant={location.pathname === `/publish/${showId || 'new-show'}/thumbnails` ? 'secondary' : 'ghost'}
              size="sm"
              className={styles.navButton}
              onClick={() => navigate(`/publish/${showId || 'new-show'}/thumbnails`)}
            >
              Thumbnails
            </Button>
            <Button 
              variant={location.pathname === `/publish/${showId || 'new-show'}/content` ? 'secondary' : 'ghost'}
              size="sm"
              className={styles.navButton}
              onClick={() => navigate(`/publish/${showId || 'new-show'}/content`)}
            >
              Content
            </Button>
            {/* Bonus Content — hidden per content team request, re-enable when needed */}
            {/* <Button
              variant={location.pathname === `/publish/${showId || 'new-show'}/extras` ? 'secondary' : 'ghost'}
              size="sm"
              className={styles.navButton}
              onClick={() => navigate(`/publish/${showId || 'new-show'}/extras`)}
            >
              Bonus Content
            </Button> */}
            <Button 
              variant={location.pathname === `/publish/${showId || 'new-show'}/preview` ? 'secondary' : 'ghost'}
              size="sm"
              className={styles.navButton}
              onClick={() => navigate(`/publish/${showId || 'new-show'}/preview`)}
            >
              Review & Publish
            </Button>
          </div>
        
          {/* Right Section */}
          <div className={styles.rightSection}>
            <Button
              variant="outline"
              size="sm"
              onClick={handleSave}
            >
              Save Draft
            </Button>
            <Button
              variant="default"
              onClick={handleContinue}
              size="sm"
            >
              Continue
            </Button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className={styles.mainContent}>
        <div className={styles.contentWrapper}>
          {/* Page Title */}
          <div className={styles.pageHeader}>
            <h2 className={styles.pageTitle}>Dubbing Details</h2>
            <div className={styles.headerActions}>
              <Button variant="ghost" size="sm" onClick={() => setShowShareModal(true)}>
                <Users className="h-4 w-4 mr-2" />
                Invite
              </Button>
              <Button 
                variant="ghost" 
                size="icon" 
                className={styles.iconButton}
                onClick={() => {
                  navigator.clipboard.writeText(window.location.href);
                }}
              >
                <Link2 className="h-4 w-4" />
              </Button>
            </div>
          </div>

          <div className={styles.sectionSpacing}>
            {dubbingApi.loading ? (
              <div className="flex items-center justify-center h-48">
                <div className="text-center">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
                  <p className="text-muted-foreground">Loading dubbing details...</p>
                </div>
              </div>
            ) : (
              <DubbingDetailsForm 
                data={state.dubbingDetails} 
                onChange={(data) => updateState({ dubbingDetails: data })} 
                showId={showId}
                onProjectCreate={async (project) => {
                  if (showId) {
                    await dubbingApi.createProject(project);
                  }
                }}
                onProjectUpdate={async (projectId, project) => {
                  if (showId) {
                    await dubbingApi.updateProject(projectId, project);
                  }
                }}
                onProjectDelete={async (projectId) => {
                  if (showId) {
                    await dubbingApi.deleteProject(projectId);
                  }
                }}
              />
            )}
            
            {/* Navigation Buttons */}
            <div className="flex justify-between items-center pt-8">
              <Button
                variant="outline"
                onClick={handleBack}
                className="flex items-center gap-2"
              >
                <ArrowLeft className="h-4 w-4" />
                Back
              </Button>
              <Button
                onClick={handleContinue}
                className="flex items-center gap-2"
              >
                Continue
                <ArrowRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>
      </div>
      <ShareModal open={showShareModal} onOpenChange={setShowShareModal} />
    </div>
  );
}
